import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { animate, frame, useMotionValue, useMotionValueEvent, usePresence, useReducedMotion } from 'motion/react';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  type RefObject,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import type { AgentRunActivityState } from '@/hooks/llm/use-llm-agent/run/agent-run-model';
import {
  chatLauncherOutcomeSeenAtom,
  chatbotOpenAtom,
  docuAiDockAtom,
  docuAiDockColumnsAtom,
  type DocuAiDockColumn,
  type DocuAiDockSlotState,
} from '@/state';

import { emphasizedTransition } from '@flintmint/web-shared/constants/ui';
import { getChatMessagesKey } from '@flintmint/web-shared/utils/llm-message-utils';

import {
  CHAT_MIN_HEIGHT,
  DEFAULT_DOCK_INSETS,
  LAUNCHER_HEIGHT,
  LAUNCHER_MIN_WIDTH,
  clampRectToBounds,
  getDockedLauncherRect,
  getLauncherPointForOpenRect,
  isDockCandidate,
  resizeRectFromEdge,
  resolveOpenRect,
  resolveOpenSize,
  type DockInsets,
  type Point,
  type Rect,
  type ResizeEdge,
} from './floating-chat-geometry';
import { useStoredFloatingChatGeometry, type StoredGeometry } from './floating-chat-storage';

/** Bounds the surface: the panel group right of the navigation rail. */
export const FLOATING_CHAT_CANVAS_ATTRIBUTE = 'data-floating-chat-canvas';
/**
 * Names the dock column (a DocuAiDockColumn value); its DocuAiDockSlot registers the element in
 * docuAiDockColumnsAtom. The column may declare its content inset in px via
 * `--floating-chat-dock-inset-right`; it defaults to the layout right panel's 12px padding.
 */
export const FLOATING_CHAT_DOCK_COLUMN_ATTRIBUTE = 'data-floating-chat-dock-column';

const DRAG_THRESHOLD = 3;
// The surface's opacity transition in llm-chat.module.scss uses the same duration (`$emphasized-transition`).
const SURFACE_FADE_MS = emphasizedTransition.duration * 1000;

/** The `transcriptKey` of an empty transcript, which has nothing for the chat to grow for. */
const EMPTY_TRANSCRIPT_KEY = getChatMessagesKey([]);

export type LauncherStatus = 'default' | 'working' | 'ready' | 'failed';
export type SurfaceInteraction = 'idle' | 'dragging' | 'resizing';

export interface UseFloatingChatSurfaceProps {
  requestId: string;
  dockColumn: DocuAiDockColumn;
  /** The surface element; written to directly while a gesture tracks the pointer. */
  surfaceRef: RefObject<HTMLElement | null>;
  isLoading: boolean;
  isNetworkError: boolean;
  activityState: AgentRunActivityState;
  /** The scrolling transcript container; measured against the open chat, which grows when it overflows. */
  messagesContainerRef: RefObject<HTMLElement | null>;
  /** Identifies the transcript's content, so a new or cleared message list is re-measured. */
  transcriptKey: string;
}

export const useFloatingChatSurface = (props: UseFloatingChatSurfaceProps) => {
  const { requestId, dockColumn, surfaceRef, isLoading, isNetworkError, activityState, messagesContainerRef, transcriptKey } =
    props;

  const [open, setOpenAtom] = useAtom(chatbotOpenAtom);
  const setDock = useSetAtom(docuAiDockAtom);
  const setDockSlot = useCallback(
    (state: DocuAiDockSlotState) =>
      setDock((dock) => (dock.column === dockColumn && dock.state === state ? dock : { column: dockColumn, state })),
    [setDock, dockColumn]
  );
  const columnElement = useAtomValue(docuAiDockColumnsAtom)[dockColumn];
  const reducedMotion = !!useReducedMotion();
  // False while the surface fades out after an ancestor AnimatePresence dropped it; an exiting surface must
  // not touch shared state. The fade is a CSS transition, so the surface tells AnimatePresence when it is over.
  const [isPresent, safeToRemove] = usePresence();
  const safeToRemoveRef = useRef(safeToRemove);
  useEffect(() => {
    safeToRemoveRef.current = safeToRemove;
  }, [safeToRemove]);
  useEffect(() => {
    if (isPresent) return;
    // Through the ref: AnimatePresence hands out a new callback on every render, which must not restart the wait.
    const timeout = setTimeout(() => safeToRemoveRef.current?.(), SURFACE_FADE_MS);
    return () => clearTimeout(timeout);
  }, [isPresent]);
  const setOpen = useCallback(
    (next: boolean) => {
      if (isPresent) setOpenAtom(next);
    },
    [isPresent, setOpenAtom]
  );

  const [stored, setStored] = useStoredFloatingChatGeometry();
  // A copy: the model is written field by field below, and `acknowledged` must keep the fields as stored.
  const model = useRef<StoredGeometry>({ ...stored });
  const session = useRef<PointerSession | null>(null);
  const suppressClick = useRef(false);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const width = useMotionValue(LAUNCHER_MIN_WIDTH);
  const height = useMotionValue(LAUNCHER_HEIGHT);

  const [active, setActive] = useState<'idle' | 'dragging' | ResizeEdge>('idle');
  const activeEdge: ResizeEdge | null = active === 'idle' || active === 'dragging' ? null : active;
  const interaction: SurfaceInteraction = activeEdge ? 'resizing' : (active as 'idle' | 'dragging');

  const readRect = useCallback(
    (): Rect => ({ x: x.get(), y: y.get(), width: width.get(), height: height.get() }),
    [x, y, width, height]
  );

  /** Where the surface is or is animating to; the motion values lag it mid-animation. */
  const targetRect = useRef<Rect | null>(null);

  /**
   * The `open` the height on screen belongs to, so the height counts as settled no earlier than the
   * commit `open` flips — ahead of the effect that re-places the surface. The transcript can only be
   * measured against the chat at rest: the open height lands a frame or more after `open` flips, and
   * measuring before then compares the transcript against the 66px launcher.
   */
  const [settledOpen, setSettledOpen] = useState<boolean | null>(null);
  /** Identifies the placement a deferred settle belongs to, so a superseded one never marks it settled. */
  const placement = useRef(0);
  const heightSettled = open && settledOpen === open;

  const applyRect = useCallback(
    (rect: Rect, animated: boolean) => {
      targetRect.current = rect;
      const animateHeight = animated && !reducedMotion;
      const values = [
        [x, rect.x],
        [y, rect.y],
        [width, rect.width],
        [height, rect.height],
      ] as const;

      // A gesture writes the element inline below, so its height is already the one on screen and stays
      // settled; every other placement lands later.
      if (!session.current) {
        const id = ++placement.current;
        setSettledOpen(null);
        if (!animateHeight) {
          frame.postRender(() => {
            if (placement.current === id) setSettledOpen(open);
          });
        }
      }

      values.forEach(([value, target]) => {
        if (animateHeight) {
          animate(value, target, emphasizedTransition);
        } else {
          value.jump(target);
        }
      });

      // While the pointer is down the surface must be the pointer: motion flushes value changes on its
      // own animation frame, which can land a frame behind the input, so the tracking phase also writes
      // the element directly. Motion's next render sets the same values.
      const element = session.current && surfaceRef.current;
      if (element) {
        element.style.transform = `translateX(${rect.x}px) translateY(${rect.y}px)`;
        element.style.width = `${rect.width}px`;
        element.style.height = `${rect.height}px`;
      }
    },
    [x, y, width, height, open, reducedMotion, surfaceRef]
  );

  // Only a completed animation is the height at rest.
  useMotionValueEvent(height, 'animationComplete', () => setSettledOpen(open));

  /**
   * The last geometry this instance put into storage or took out of it. The model is mutated afterwards
   * (syncRect derives `openOffset` into it) and is therefore always a copy, so this snapshot keeps the
   * fields as they were written and can tell this instance's own echo from a write from somewhere else.
   */
  const acknowledged = useRef(stored);

  const persist = useCallback(() => {
    const next = { ...model.current };
    acknowledged.current = next;
    setStored(next);
  }, [setStored]);

  const resolveLauncherRect = useCallback((bounds: Rect, column: DockColumn | null): Rect => {
    if (model.current.docked) {
      // With no dock column (right panel hidden), keep the Figma insets against the canvas instead.
      return clampRectToBounds(
        column ? getDockedLauncherRect(column.rect, column.insets) : getDockedLauncherRect(bounds),
        bounds
      );
    }

    // Free-floating collapsed launcher is always 336px; sidebar width does not affect it.
    return clampRectToBounds({ ...model.current.launcher, width: LAUNCHER_MIN_WIDTH, height: LAUNCHER_HEIGHT }, bounds);
  }, []);

  /** Launcher rect at the last derivation; a docked chat follows it when the column moves. */
  const anchorLauncher = useRef<Rect | null>(null);

  // A transcript that outgrew the chat grows it, and the grown height sticks for the rest of the request:
  // the chat must not shrink back the moment the taller surface fits its messages again. The latch belongs
  // to the transcript it was measured on, so another note — this surface outlives note navigation — and a
  // cleared transcript both start ungrown again. Never persisted.
  const [hasOverflowed, setHasOverflowed] = useState(false);
  const transcriptEmpty = transcriptKey === EMPTY_TRANSCRIPT_KEY;
  useEffect(() => {
    setHasOverflowed(false);
  }, [requestId, transcriptEmpty]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (hasOverflowed || !heightSettled || !container) return;

    // Bounds too short for the open height cap the surface, and a transcript overflowing that capped
    // height says nothing about the chat's own size: growing off it would stick once the bounds are back.
    const applied = targetRect.current ?? readRect();
    const { size, docked, customSize } = model.current;
    if (applied.height < resolveOpenSize(size, { docked, customSize, hasOverflowed: false }).height) return;

    if (container.scrollHeight > container.clientHeight + 1) setHasOverflowed(true);
  }, [heightSettled, transcriptKey, hasOverflowed, messagesContainerRef, readRect]);

  /**
   * Derives the rect from the model: first placement, open/collapse transitions, and a column appearing.
   * A free-floating chat grows out of its stored launcher's bottom edge and collapses back onto it, so the
   * open rect and the launcher are always the same pair however the bounds clamped either in between. A
   * docked open chat instead sits at its remembered offset from the dock slot, so every instance (note
   * chat, block chat, a reopen) lands on the same rect; docked chats without a custom size take their
   * width from the launcher so they stay matched, and a grown one keeps the same placement, raised by
   * `getGrowthRise` so it grows out of its bottom edge.
   */
  const syncRect = useCallback(
    (animated: boolean) => {
      const bounds = getCanvasRect();
      const launcher = resolveLauncherRect(bounds, measureDockColumn(columnElement));
      anchorLauncher.current = launcher;

      if (!open) {
        return applyRect(launcher, animated);
      }

      const { size, openOffset, docked, customSize } = model.current;
      const openSize = resolveOpenSize(size, { docked, customSize, hasOverflowed });

      // A free chat is placed straight from its launcher, at whatever height it resolves to: the grown
      // height is what is placed, so its bottom stays on the launcher's bottom edge with no separate rise.
      if (!docked) {
        return applyRect(resolveOpenRect(launcher, openSize, bounds), animated);
      }

      const growth = getGrowthRise(openSize.height, hasOverflowed && !customSize);
      if (openOffset) {
        return applyRect(
          clampRectToBounds({ x: launcher.x + openOffset.x, y: launcher.y + openOffset.y - growth, ...openSize }, bounds),
          animated
        );
      }

      const rect = resolveOpenRect(launcher, openSize, bounds);
      // The offset always describes the ungrown chat, so it is resolved from the ungrown placement rather
      // than read off this rect.
      const ungrown = growth ? resolveOpenRect(launcher, { ...openSize, height: CHAT_MIN_HEIGHT }, bounds) : rect;
      // Zero for a chat that was never resized: it is exactly as wide as its launcher, so the same clamp
      // lands both on the same x.
      model.current.openOffset = { x: ungrown.x - launcher.x, y: ungrown.y - launcher.y };
      applyRect(rect, animated);
    },
    [open, columnElement, hasOverflowed, resolveLauncherRect, applyRect]
  );

  /**
   * Re-clamps after the bounds change. The model is the user's intent — the launcher they left the chat on
   * and the size they gave it — and the on-screen rect is derived and clamped from that, so a transient
   * clamp is never persisted and everything is simply re-derived: bounds that once capped the chat stop
   * capping it, in size and in placement, when they grow back.
   * The exception is a docked chat the user resized: it owns an on-screen position no model field
   * describes, so it keeps its on-screen (or animation-target) rect and just follows the dock column.
   */
  const settleRect = useCallback(() => {
    const { docked, customSize } = model.current;
    if (!open || !docked || !customSize) {
      return syncRect(false);
    }

    const bounds = getCanvasRect();
    const launcher = resolveLauncherRect(bounds, measureDockColumn(columnElement));
    const previous = anchorLauncher.current ?? launcher;
    anchorLauncher.current = launcher;

    const current = targetRect.current ?? readRect();

    applyRect(
      clampRectToBounds(
        {
          x: current.x + launcher.x - previous.x,
          y: current.y + launcher.y - previous.y,
          width: current.width,
          height: current.height,
        },
        bounds
      ),
      false
    );
  }, [open, columnElement, syncRect, resolveLauncherRect, readRect, applyRect]);

  // Reserve the dock slot whenever the chat is docked, including while open, so sidebar content
  // does not jump when expanding. Dragging drives placeholder / hidden instead.
  const reserveDockSlot = useCallback(() => {
    if (!isPresent || interaction !== 'idle') return;
    setDockSlot(model.current.docked ? 'docked' : 'hidden');
  }, [isPresent, interaction, setDockSlot]);
  useEffect(() => {
    reserveDockSlot();
  }, [reserveDockSlot]);

  // Geometry persisted elsewhere (another tab, or the other chat instance) is adopted between gestures.
  // The rule: adopt anything that differs from what this instance last persisted or adopted. Comparing
  // against that snapshot rather than the live model is what makes the rule hold, because the model is
  // mutated in place afterwards — a docked open derives `openOffset` into it — while the snapshot keeps the
  // fields as they were written. So this instance's own echo always matches and is skipped (a re-place
  // here would cut the open/collapse animation short), and a peer's write is adopted whatever it says,
  // including a cleared `openOffset`: a collapsed dock-drop legitimately persists one.
  useEffect(() => {
    if (session.current || isSameGeometry(stored, acknowledged.current)) return;
    acknowledged.current = stored;
    model.current = { ...stored };
    syncRect(false);
    // Adopted geometry can flip `docked`, which the effect above cannot see: the model is a ref.
    reserveDockSlot();
  }, [stored, syncRect, reserveDockSlot]);

  // The first placement and a dock column appearing/disappearing (route change) jump; open/collapse
  // transitions animate.
  const hasPositioned = useRef(false);
  const positionedColumn = useRef(columnElement);
  useLayoutEffect(() => {
    syncRect(hasPositioned.current && positionedColumn.current === columnElement);
    hasPositioned.current = true;
    positionedColumn.current = columnElement;
  }, [syncRect, columnElement]);

  // Only the owner releases the dock: when the overlay closes, the layout chat docks before the
  // overlay chat finishes its exit animation and unmounts.
  const releaseDock = useCallback(
    () => setDock((dock) => (dock.column === dockColumn ? { column: null, state: 'hidden' } : dock)),
    [setDock, dockColumn]
  );
  useEffect(() => {
    if (!isPresent) releaseDock();
  }, [isPresent, releaseDock]);
  useEffect(() => releaseDock, [releaseDock]);

  // Subscribed per column element and reading settleRect through a ref: an observer re-created on
  // open/collapse would fire immediately and cut the animation short. The ref is refreshed in a layout
  // effect because the observer can fire before passive effects run, and would then settle against a
  // stale closure — last render's `open`, dock column, or grown height.
  const settleRectRef = useRef(settleRect);
  useLayoutEffect(() => {
    settleRectRef.current = settleRect;
  }, [settleRect]);

  useEffect(() => {
    const onBoundsChange = () => {
      if (session.current) return;
      settleRectRef.current();
    };

    const observer = new ResizeObserver(onBoundsChange);
    [getCanvasElement(), columnElement].forEach((element) => element && observer.observe(element));
    window.addEventListener('resize', onBoundsChange);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onBoundsChange);
    };
  }, [columnElement]);

  // Launcher status is derived, not remembered: the agent run store keeps the finished run's
  // activityState under its request id until the next run starts, and whether the user has already
  // seen that outcome lives in a page-lifetime atom.
  const [seenOutcomes, setSeenOutcomes] = useAtom(chatLauncherOutcomeSeenAtom);
  useEffect(() => {
    // A run in flight leaves an outcome the user has not seen; opening the chat is where they see it.
    // Collapsed with no run in flight changes nothing.
    if (!isPresent || (!isLoading && !open)) return;

    const seen = !isLoading;
    setSeenOutcomes((current) => (current[requestId] === seen ? current : { ...current, [requestId]: seen }));
  }, [isPresent, isLoading, open, requestId, setSeenOutcomes]);

  const launcherStatus = resolveLauncherStatus({
    open,
    isLoading,
    activityState,
    isNetworkError,
    outcomeSeen: !!seenOutcomes[requestId],
  });

  const takeSession = (event: ReactPointerEvent<HTMLElement>) => {
    const current = session.current;
    if (!current || current.pointerId !== event.pointerId) return null;

    session.current = null;
    // Releasing a capture the element no longer holds throws NotFoundError (a touch or pen pointer stops
    // existing when the contact ends), which would strand the gesture before it is committed or rolled back.
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    return current;
  };

  /** pointerup: commit the gesture. */
  const finishSession = (event: ReactPointerEvent<HTMLElement>) => {
    const current = takeSession(event);
    if (!current?.started) return;

    suppressClick.current = true;
    const rect = readRect();

    if (current.kind === 'drag' && current.dockCandidate) {
      model.current.docked = true;
      // Drop free-floating openOffset so syncRect re-derives against the docked launcher (snaps into place).
      model.current.openOffset = null;
    } else if (current.kind === 'drag') {
      // A dragged surface leaves the dock.
      model.current.docked = false;
    }

    if (current.kind === 'resize') {
      model.current.size = { width: rect.width, height: rect.height };
      model.current.customSize = true;

      if (model.current.docked) {
        // The dock launcher stays put; the chat remembers where it now sits relative to it.
        const launcher = anchorLauncher.current ?? resolveLauncherRect(current.bounds, current.column);
        model.current.openOffset = { x: rect.x - launcher.x, y: rect.y - launcher.y };
      }
    }

    // A gesture is the only thing that moves a free-floating chat's launcher: it lands on the surface's
    // bottom-left, which for a collapsed one is the launcher itself. Everything else — an open, a collapse,
    // a bounds change — is derived from it, so a clamp never becomes the user's stored position.
    if (!model.current.docked) {
      model.current.openOffset = null;
      model.current.launcher = getLauncherPointForOpenRect(rect);
    }

    persist();
    setActive('idle');

    if (current.kind === 'drag' && current.dockCandidate) {
      syncRect(true);
      // syncRect derives a fresh openOffset against the docked launcher; persist it.
      persist();
    } else {
      // Bounds events are ignored while a session runs.
      settleRect();
    }
  };

  /**
   * pointercancel / capture lost. A genuine cancellation (the browser took the gesture, the pointer is
   * still down) puts everything back; a capture loss with the button already released is a drop that
   * never reached pointerup, so it commits like one.
   */
  const cancelSession = (event: ReactPointerEvent<HTMLElement>) => {
    const released = event.type === 'lostpointercapture' && event.buttons === 0;
    if (released) {
      return finishSession(event);
    }

    const current = takeSession(event);
    if (!current?.started) return;

    applyRect(current.startRect, false);
    // Returning to idle re-derives the slot state from the (unchanged) model.
    setActive('idle');
  };

  const movePointer = (event: ReactPointerEvent<HTMLElement>) => {
    const current = session.current;
    if (!current || current.pointerId !== event.pointerId) return;

    const dx = event.clientX - current.start.x;
    const dy = event.clientY - current.start.y;

    if (!current.started) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      current.started = true;
      setActive(current.kind === 'drag' ? 'dragging' : current.edge);
      if (current.kind === 'drag') {
        setDockSlot('hidden');
      }
    }

    const next =
      current.kind === 'drag'
        ? clampRectToBounds({ ...current.startRect, x: current.startRect.x + dx, y: current.startRect.y + dy }, current.bounds)
        : resizeRectFromEdge(current.startRect, current.edge, dx, dy, current.bounds);

    if (current.kind === 'drag') {
      const candidate = isDockCandidate(next, current.column?.rect ?? null);
      if (candidate !== current.dockCandidate) {
        current.dockCandidate = candidate;
        setDockSlot(candidate ? 'placeholder' : 'hidden');
      }
    }

    applyRect(next, false);
  };

  const beginSession = (event: ReactPointerEvent<HTMLElement>, gesture: Gesture) => {
    if (event.button !== 0 || session.current || !isPresent) return;

    // A press mid-animation must start from the settled rect.
    settleRect();

    event.currentTarget.setPointerCapture?.(event.pointerId);
    suppressClick.current = false;
    session.current = {
      ...gesture,
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      startRect: readRect(),
      bounds: getCanvasRect(),
      column: measureDockColumn(columnElement),
      started: false,
      dockCandidate: false,
    };
  };

  const pointerHandlers = {
    onPointerMove: movePointer,
    onPointerUp: finishSession,
    onPointerCancel: cancelSession,
    onLostPointerCapture: cancelSession,
  };

  const getMoveHandleProps = () => ({
    ...pointerHandlers,
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => beginSession(event, { kind: 'drag' }),
  });

  const getResizeHandleProps = (edge: ResizeEdge) => ({
    ...pointerHandlers,
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      beginSession(event, { kind: 'resize', edge });
    },
  });

  /** True once after a completed drag, so the click that follows it does not open the chat. */
  const consumeSuppressedClick = () => {
    const suppressed = suppressClick.current;
    suppressClick.current = false;
    return suppressed;
  };

  /** A press outside the move handle is never a drag and must not inherit a stale suppression. */
  const clearSuppressedClick = () => {
    suppressClick.current = false;
  };

  return {
    open,
    setOpen,
    isPresent,
    style: { x, y, width, height },
    interaction,
    activeEdge,
    launcherStatus,
    getMoveHandleProps,
    getResizeHandleProps,
    consumeSuppressedClick,
    clearSuppressedClick,
  };
};

/**
 * A docked chat's `openOffset` is always expressed at the ungrown open height, so it stays
 * height-independent and safe to persist across a grow or shrink. Zero whenever the chat is not grown.
 */
const getGrowthRise = (openHeight: number, grown: boolean) => (grown ? openHeight - CHAT_MIN_HEIGHT : 0);

const resolveLauncherStatus = ({
  open,
  isLoading,
  activityState,
  isNetworkError,
  outcomeSeen,
}: Pick<UseFloatingChatSurfaceProps, 'isLoading' | 'isNetworkError' | 'activityState'> & {
  open: boolean;
  outcomeSeen: boolean;
}): LauncherStatus => {
  if (isLoading) return 'working';
  if (open || outcomeSeen) return 'default';
  if (activityState === 'done') return 'ready';
  if (activityState === 'errored' || isNetworkError) return 'failed';
  return 'default';
};

type DockColumn = { rect: Rect; insets: DockInsets };

type Gesture = { kind: 'drag'; edge?: undefined } | { kind: 'resize'; edge: ResizeEdge };

type PointerSession = Gesture & {
  pointerId: number;
  start: Point;
  startRect: Rect;
  bounds: Rect;
  column: DockColumn | null;
  started: boolean;
  dockCandidate: boolean;
};

const isSameGeometry = (stored: StoredGeometry, model: StoredGeometry) =>
  stored.docked === model.docked &&
  stored.launcher.x === model.launcher.x &&
  stored.launcher.y === model.launcher.y &&
  stored.size.width === model.size.width &&
  stored.size.height === model.size.height &&
  stored.customSize === model.customSize &&
  stored.openOffset?.x === model.openOffset?.x &&
  stored.openOffset?.y === model.openOffset?.y;

const getCanvasRect = (): Rect => {
  const element = getCanvasElement();
  return element ? toRect(element) : { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
};

const measureDockColumn = (element: HTMLElement | null): DockColumn | null => {
  if (!element) return null;

  const style = getComputedStyle(element);
  const inset = (name: string, fallback: number) => {
    const value = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  return {
    rect: toRect(element),
    insets: { right: inset('--floating-chat-dock-inset-right', DEFAULT_DOCK_INSETS.right) },
  };
};

const getCanvasElement = () => document.querySelector<HTMLElement>(`[${FLOATING_CHAT_CANVAS_ATTRIBUTE}]`);

const toRect = (element: Element): Rect => {
  const { left, top, width, height } = element.getBoundingClientRect();
  return { x: left, y: top, width, height };
};
